/**
 * Where the marks go. Arithmetic only, so it can be checked without a browser.
 *
 * Drawn as SVG rather than with a charting library, deliberately. The console
 * has no chart dependency, adding one to this programme's tree is a decision
 * with a supply chain attached, and the geometry is where the mistakes are: a
 * scale that hides a spike, a null drawn as a zero, an axis whose labels do not
 * match its bars. Those are checkable here and would not be through a canvas.
 *
 * THE PLOT IS SIZED IN REAL PIXELS. The first version drew into a fixed 720 wide
 * viewBox and let the browser scale it, so on a wide card every bar, line and
 * label grew with the card: 30 px bars, 16 px axis text, a chart that filled the
 * page. Now the caller measures its container and the plot is drawn at that
 * width, so a bar stays a bar and text stays text at any size.
 */

export interface Plot {
  width: number
  height: number
  padTop: number
  padBottom: number
  padLeft: number
  padRight: number
}

export function plotFor(width: number, height: number): Plot {
  return {
    width: Math.max(160, Math.round(width)),
    height,
    padTop: 10,
    padBottom: 24,
    padLeft: 44,
    padRight: 10,
  }
}

export function innerWidth(plot: Plot): number {
  return plot.width - plot.padLeft - plot.padRight
}

export function innerHeight(plot: Plot): number {
  return plot.height - plot.padTop - plot.padBottom
}

/**
 * A value axis with clean ticks: 0, 100, 200, 300, 400 rather than 0 and 361.
 *
 * Never less than 1 high, so a range where nothing happened draws an empty
 * chart with a sensible axis rather than dividing by zero. Gaps are not
 * zeros and are ignored when finding the top.
 */
export function niceScale(
  values: Array<number | null>,
  tickCount = 4,
): { top: number; ticks: number[] } {
  const present = values.filter((value): value is number => value !== null && Number.isFinite(value))
  const max = Math.max(0, ...present)
  if (max <= 0) return { top: 1, ticks: [0, 1] }
  const raw = max / tickCount
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const step =
    [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((candidate) => candidate >= raw) ??
    10 * magnitude
  const top = Math.ceil(max / step) * step
  const ticks: number[] = []
  for (let tick = 0; tick <= top + step / 2; tick += step) ticks.push(Number(tick.toFixed(10)))
  return { top, ticks }
}

/** The x of each bucket's band, and the width of one band. */
export function band(count: number, plot: Plot): {
  width: number
  at: (index: number) => number
  centre: (index: number) => number
} {
  const width = count > 0 ? innerWidth(plot) / count : innerWidth(plot)
  return {
    width,
    at: (index) => plot.padLeft + index * width,
    centre: (index) => plot.padLeft + index * width + width / 2,
  }
}

/**
 * How thick a bar is inside its band. Capped at 24 px and never the whole band:
 * the leftover is air, and a chart of solid blocks reads loud and childish.
 */
export function barWidth(bandWidth: number): number {
  return Math.max(2, Math.min(24, bandWidth * 0.66))
}

/** The y for a value, with 0 at the bottom of the plot. */
export function y(value: number, top: number, plot: Plot): number {
  const share = top === 0 ? 0 : value / top
  return plot.padTop + innerHeight(plot) * (1 - share)
}

/** Which bucket the pointer is over, clamped to the plot. */
export function bucketAt(x: number, count: number, plot: Plot): number {
  if (count <= 0) return -1
  const { width } = band(count, plot)
  const index = Math.floor((x - plot.padLeft) / width)
  return Math.max(0, Math.min(count - 1, index))
}

/**
 * A line as SVG path segments, broken wherever the data is null.
 *
 * Broken, not bridged. A line drawn straight through a gap asserts a value for
 * a period that has none, and on a latency chart that invented segment is the
 * flattest, calmest part of the picture, lying exactly where there was nothing
 * to measure. A single point with gaps either side is not a line and draws
 * nothing; its value is still in the tooltip and the table.
 */
export function pathSegments(
  values: Array<number | null>,
  top: number,
  plot: Plot,
): string[] {
  const { centre } = band(values.length, plot)
  const segments: string[] = []
  let current: string[] = []
  values.forEach((value, index) => {
    if (value === null || !Number.isFinite(value)) {
      if (current.length > 1) segments.push(current.join(' '))
      current = []
      return
    }
    const point = `${centre(index).toFixed(1)},${y(value, top, plot).toFixed(1)}`
    current.push(current.length === 0 ? `M${point}` : `L${point}`)
  })
  if (current.length > 1) segments.push(current.join(' '))
  return segments
}

/**
 * A bar segment as a path: square at the bottom, and a 4 px rounded top only
 * when it is the top of its stack. Rounding every segment would put notches
 * inside the bar where two segments meet.
 */
export function barPath(
  x: number,
  width: number,
  yTop: number,
  yBottom: number,
  roundTop: boolean,
): string {
  const height = Math.max(0, yBottom - yTop)
  const r = roundTop ? Math.min(4, width / 2, height) : 0
  if (r === 0) return `M${x},${yBottom} V${yTop} H${x + width} V${yBottom} Z`
  return [
    `M${x},${yBottom}`,
    `V${yTop + r}`,
    `Q${x},${yTop} ${x + r},${yTop}`,
    `H${x + width - r}`,
    `Q${x + width},${yTop} ${x + width},${yTop + r}`,
    `V${yBottom}`,
    'Z',
  ].join(' ')
}

// ── time ──────────────────────────────────────────────────────────────────

function parts(moment: string, zone: string) {
  const formatted = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: 'short',
    weekday: 'short',
    hourCycle: 'h23',
  }).formatToParts(new Date(moment))
  const get = (type: string) => formatted.find((part) => part.type === type)?.value ?? ''
  return {
    hour: Number(get('hour')),
    minute: get('minute'),
    day: get('day'),
    month: get('month'),
    weekday: get('weekday'),
  }
}

export interface TimeTick {
  index: number
  label: string
  /** A day boundary on an hourly axis, or a month boundary on a daily one.
   *  Drawn a shade stronger so the reader sees where one day ends. */
  major: boolean
}

/**
 * Which buckets get a label, and what it says, in the zone the page renders.
 *
 * Labels sit on round boundaries (every 3 or 6 hours, every 2 or 7 days) rather
 * than on every nth bucket, so the same hour carries the same label whatever
 * range is on screen. Midnight is labelled with the day instead of 00:00: an
 * hourly axis spanning two days that only ever says "09:00" has no way to say
 * which 09:00 it means.
 */
export function timeTicks(
  buckets: string[],
  bucketSeconds: number,
  zone: string,
  width: number,
): TimeTick[] {
  if (buckets.length === 0) return []
  // One label per 48 px: a day by the hour reads every three hours on a half
  // width card and every six on a phone, and an "18:00" is about 32 px wide.
  const room = Math.max(2, Math.floor(width / 48))
  const hourly = bucketSeconds < 86400
  const steps = hourly ? [1, 2, 3, 6, 12, 24] : [1, 2, 7, 14, 28]
  const step = steps.find((candidate) => buckets.length / candidate <= room) ?? steps[steps.length - 1]

  const ticks: TimeTick[] = []
  buckets.forEach((moment, index) => {
    const p = parts(moment, zone)
    if (hourly) {
      const midnight = p.hour === 0
      if (p.hour % step !== 0 && !midnight) return
      ticks.push({
        index,
        label: midnight ? `${p.weekday} ${Number(p.day)}` : `${String(p.hour).padStart(2, '0')}:00`,
        major: midnight,
      })
      return
    }
    const dayOfMonth = Number(p.day)
    const monthStart = dayOfMonth === 1
    if (!monthStart && (dayOfMonth - 1) % step !== 0) return
    ticks.push({ index, label: `${dayOfMonth} ${p.month}`, major: monthStart })
  })
  return ticks
}

/** A bucket as the tooltip names it: which hour, or which day, in words. */
export function spanLabel(moment: string, bucketSeconds: number, zone: string): string {
  const start = parts(moment, zone)
  const date = `${start.weekday} ${Number(start.day)} ${start.month}`
  if (bucketSeconds >= 86400) return date
  const end = parts(new Date(new Date(moment).getTime() + bucketSeconds * 1000).toISOString(), zone)
  const hh = (hour: number) => String(hour).padStart(2, '0')
  return `${date}, ${hh(start.hour)}:00 to ${hh(end.hour)}:00`
}

/**
 * A dragged selection as the range it names.
 *
 * Inclusive of both buckets the drag touched, so selecting the 14:00 and 16:00
 * bars asks for 14:00 until 17:00: the end of the last bar, not its start.
 * Dragging leftwards is the same selection as dragging rightwards.
 */
export function selectionRange(
  a: number,
  b: number,
  buckets: string[],
  bucketSeconds: number,
): { from: string; to: string } | null {
  if (buckets.length === 0) return null
  const first = Math.max(0, Math.min(a, b))
  const last = Math.min(buckets.length - 1, Math.max(a, b))
  const from = new Date(buckets[first])
  const to = new Date(new Date(buckets[last]).getTime() + bucketSeconds * 1000)
  return { from: from.toISOString(), to: to.toISOString() }
}
